import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import classNames from "classnames";

import Button from "components/library/Button";
import DropdownMenu from "components/library/DropdownMenu";
import IconButton from "components/library/IconButton";
import { STATUS } from "config/constants";
import { API_TO_URL, VALIDATION_MESSAGE } from "config/constants/editor";
import { updateFormAt, validateFormAsync } from "editor/actions/form";
import { unlockItem } from "editor/actions/locking";
import { saveForm } from "editor/actions/status";
import copy from "utils/clipboard";
import { sendNewsletterAsync } from "editor/actions/newsletter";
import { setMessage } from "actions/message";
import permissionsMatch from "utils/permissionsMatch";
import isDateNotInPast from "editor/utils/isDateNotInPast";

import styles from "./EditorCTA.css";

class EditorCTA extends Component {
  constructor(props) {
    super(props);

    this.saveWithUpdate = this._saveWithUpdate.bind(this);
    this.copyHandler = this._copyHandler.bind(this);
    this.toggleMoreOptions = this._toggleMoreOptions.bind(this);
    this.newsletterSend = this._newsletterSend.bind(this);
    this.validateAndRedirect = this._validateAndRedirect.bind(this);

    this.state = {
      moreOptionsVisible: false,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      url: `/editor/${API_TO_URL[nextProps.status.editorType.name]}/${
        nextProps.form.id
      }`,
    });
  }

  _toggleMoreOptions() {
    this.setState({ moreOptionsVisible: !this.state.moreOptionsVisible });
  }

  handleErrs(errors) {
    return Promise.reject(
      errors && Object.values(errors).length
        ? Object.values(errors)[0]
        : new Error(VALIDATION_MESSAGE)
    );
  }

  _saveWithUpdate(formKey, formValue, redirect) {
    // This object spread is a _big_ oversimplification of what updateFormAt does,
    // but none of the callers in this component use nested keys ("foo.bar") so it should work ok
    return this.props
      .validateFormAsync({ ...this.props.form, formKey: formValue })
      .catch(this.handleErrs)
      .then(() => {
        if (formKey) {
          return this.props.updateFormAt(formKey, formValue);
        } else {
          return Promise.resolve();
        }
      })
      .then(() => this.props.saveForm())
      .then(() => {
        if (redirect) {
          this.props.unlockItem(this.props.form.id);
          this.props.history.push(redirect);
        }
      })
      .catch(error => this.props.setMessage({ message: error }));
  }

  _copyHandler(e) {
    e.preventDefault();
    copy(this.props.form.permalink);
  }

  statusButton(status, label) {
    return (
      <div
        role="button"
        onClick={() => this.saveWithUpdate("status", status.slug)}
      >
        {label}
      </div>
    );
  }

  _newsletterSend(live = false) {
    return this.props
      .validateFormAsync(this.props.form)
      .catch(this.handleErrs)
      .then(() => this.props.saveForm())
      .then(() => {
        if (live) {
          this.props.history.push(`${this.state.url}/send`);
        } else {
          return this.props
            .sendNewsletterAsync(this.props.form.id, live)
            .then(() => {
              this.props.setMessage({
                message: "Your test email was sent",
                type: "success",
              });
              return this.saveWithUpdate("test_sent", true);
            });
        }
      })
      .catch(err => this.props.setMessage({ message: err }));
  }

  _validateAndRedirect(newPath, publishAsBreaking = false) {
    return this.props
      .validateFormAsync(this.props.form, publishAsBreaking)
      .catch(this.handleErrs)
      .then(() => this.props.saveForm())
      .then(() => this.props.history.push(`${this.state.url}/${newPath}`))
      .catch(error => this.props.setMessage({ message: error }));
  }

  render() {
    if (this.props.form.status === STATUS.DELETED.slug) return null;

    const embargoTrue = isDateNotInPast(
      this.props.form.embargo_until,
      "minute"
    );

    const preview = this.props.form.id ? (
      <Link
        data-cy="publishLink"
        to={`${this.state.url}/preview`}
        target="_blank"
        className={"gtm-preview"}
      >
        Preview
      </Link>
    ) : null;
    const copy = (
      <div role="button" onClick={this.copyHandler} className={"gtm-copy-link"}>
        Copy link
      </div>
    );
    const unpublish = (
      <Link
        to={`${this.state.url}/unpublish`}
        className={classNames(styles.warning, "gtm-unpublish")}
      >
        Unpublish
      </Link>
    );
    const publish = embargoTrue ? null : (
      <div
        role="button"
        onClick={() => this.validateAndRedirect("publish")}
        className={"gtm-publish"}
      >
        Publish
      </div>
    );
    const publishAsBreaking = embargoTrue
      ? null
      : permissionsMatch({
          permissions: this.props.permissions,
          required: ["content.publish-breaking-news"],
        }) && (
          <div
            role="button"
            onClick={() => this.validateAndRedirect("breaking", true)}
            className={"gtm-publish-breaking-news"}
          >
            Publish as breaking news
          </div>
        );
    const deleteDraft = (
      <Link
        data-cy="deleteDraftButton"
        to={this.props.form.id ? `${this.state.url}/delete` : "/dashboard"}
        className={classNames(styles.warning, "gtm-delete-draft")}
      >
        Delete draft
      </Link>
    );
    const schedule = (
      <div
        role="button"
        onClick={() => this.validateAndRedirect("schedule")}
        className="gtm-schedule"
      >
        Schedule
      </div>
    );
    const unschedule = (
      <Link to={`${this.state.url}/unschedule`} className={"gtm-unschedule"}>
        Unschedule
      </Link>
    );
    const alert = (
      <div
        role="button"
        onClick={() => this.validateAndRedirect("send-alert")}
        className={"gtm-send-alert"}
      >
        Send alert
      </div>
    );
    const sendTestEmail = (
      <div
        role="button"
        onClick={() => this.newsletterSend()}
        className={"gtm-send-test-email"}
      >
        Send test email
      </div>
    );
    const sendAndPublish = (
      <div
        role="button"
        onClick={() => this.newsletterSend(true)}
        className={"gtm-send-and-publish"}
        data-cy="send-and-publish"
      >
        Send & publish
      </div>
    );
    const resend = (
      <div
        role="button"
        onClick={() => this.newsletterSend(true)}
        className={"gtm-resend"}
      >
        Resend
      </div>
    );

    const buttonStates = {
      [STATUS.UNASSIGNED.slug]: {
        story: {
          description: "Ready for edits",
          action: () =>
            this.saveWithUpdate("status", STATUS.READY_FOR_EDITS.slug, "/"),
          secondary: [
            publish,
            publishAsBreaking,
            schedule,
            preview,
            deleteDraft,
          ],
        },
        newsletter: {
          description: "Ready for edits",
          action: () =>
            this.saveWithUpdate("status", STATUS.READY_FOR_EDITS.slug, "/"),
          secondary: [
            sendTestEmail,
            preview,
            sendAndPublish,
            schedule,
            deleteDraft,
          ],
        },
      },
      [STATUS.IN_PROGRESS.slug]: {
        story: this.props.form.in_edits
          ? {
              description: "Publish",
              action: () => this.validateAndRedirect("publish"),
              secondary: [publishAsBreaking, preview, deleteDraft],
            }
          : {
              description: "Ready for edits",
              action: () =>
                this.saveWithUpdate("status", STATUS.READY_FOR_EDITS.slug, "/"),
              secondary: [
                publish,
                publishAsBreaking,
                schedule,
                preview,
                deleteDraft,
              ],
            },
        newsletter: this.props.form.in_edits
          ? this.props.form.test_sent
            ? {
                description: "Send & publish",
                action: () => this.newsletterSend(true),
                secondary: [sendTestEmail, preview, deleteDraft],
              }
            : {
                description: "Send test email",
                action: () => this.newsletterSend(),
                secondary: [sendAndPublish, schedule, deleteDraft],
              }
          : {
              description: "Ready for edits",
              action: () =>
                this.saveWithUpdate("status", STATUS.READY_FOR_EDITS.slug, "/"),
              secondary: [
                sendTestEmail,
                preview,
                sendAndPublish,
                schedule,
                deleteDraft,
              ],
            },
      },
      [STATUS.READY_FOR_EDITS.slug]: {
        story: {
          description: "Publish",
          action: () => this.validateAndRedirect("publish"),
          secondary: [publishAsBreaking, preview, deleteDraft],
        },
        newsletter: this.props.form.test_sent
          ? {
              description: "Send & publish",
              action: () => this.newsletterSend(true),
              secondary: [sendTestEmail, preview, deleteDraft],
            }
          : {
              description: "Send test email",
              action: () => this.newsletterSend(),
              secondary: [sendAndPublish, schedule, deleteDraft],
            },
      },
      [STATUS.SCHEDULED.slug]: {
        story: {
          description: "Schedule",
          action: () => this.validateAndRedirect("schedule"),
          secondary: [copy, publish, preview, unschedule],
        },
        newsletter: {
          description: "Schedule",
          action: () => this.validateAndRedirect("schedule"),
          secondary: [sendAndPublish, preview, unschedule],
        },
      },
      [STATUS.PUBLISHED.slug]: {
        story: {
          description: "Update",
          action: () => this.validateAndRedirect("update"),
          secondary: [copy, alert, preview, unpublish],
        },
        newsletter: {
          description: "Update",
          action: () => this.validateAndRedirect("update"),
          secondary: [copy, resend, sendTestEmail, preview, unpublish],
        },
      },
    };

    let buttons = {
      description: "Ready for edits",
      action: () => this.saveWithUpdate("status", STATUS.READY_FOR_EDITS.slug),
      secondary: [publish, publishAsBreaking, preview, deleteDraft],
    };

    if (this.props.form.status) {
      buttons = buttonStates[this.props.form.status][this.props.type];
    }

    return (
      <div className={styles.root}>
        <Button
          data-testid="primary"
          isDisabled={this.props.status.isReadOnly || embargoTrue}
          onClick={buttons.action}
          data-cy="ctaActionButton"
          type="primary"
        >
          {buttons.description}
        </Button>
        <IconButton
          className="gtm-more-options"
          data-cy="moreOptionsButton"
          disabled={this.props.status.isReadOnly}
          name="more-horizontal"
          onClick={this.toggleMoreOptions}
        />
        <div className={styles.positionDropdown}>
          <DropdownMenu
            className={styles.moreOptions}
            isVisible={this.state.moreOptionsVisible}
            onClose={this.toggleMoreOptions}
            closeOnFirstClick={true}
          >
            {React.Children.map(buttons.secondary, item => item)}
          </DropdownMenu>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  form: state.editor.form,
  status: state.editor.status,
  permissions: state.user.permissions,
});

const mapDispatchToProps = {
  updateFormAt,
  saveForm,
  setMessage,
  sendNewsletterAsync,
  validateFormAsync,
  unlockItem,
};

EditorCTA.propTypes = {
  form: PropTypes.object,
  status: PropTypes.object,
  updateFormAt: PropTypes.func,
  saveForm: PropTypes.func,
  type: PropTypes.oneOf(["story", "newsletter"]),
  history: PropTypes.object.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditorCTA);
